'use client'

import { useMutation } from '@apollo/client/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import Cookies from 'universal-cookie'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog'
import { MutationStatus, QueryError, convertGraphqlErrorToQueryError } from '../../lib/hooks/types'
import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager/useGoogleTagManager'
import {
  AccountAuthorization,
  convertCookiesToAccountAuthorization,
} from '../_config/Authentication.config'
import { cookieKeys } from '../_config/Cookies.config'

import { useCookies } from './CookiesProvider.client'
import { useGraphqlClients } from './GraphqlClientsProvider.client'

type Destination = string | URL | (() => void)

const navigateToDestination = (destination: Destination) => {
  if (typeof destination === 'function') {
    destination()
  } else if (typeof destination === 'string') {
    window.location.href = destination
  } else {
    window.location.href = destination.toString()
  }
}

export type UseAuthenticationOutput = {
  account: AccountAuthorization | undefined
  isLoggedIn: boolean
  refreshAuthTokens: () => Promise<void>
  refreshAuthTokensStatus: MutationStatus
  logOut: (options?: { destination?: Destination }) => void
  logoutMutationStatus: MutationStatus
  logIn: (credentials: { email: string; password: string; destination?: Destination }) => void
  loginMutationErrors: QueryError[]
  loginMutationStatus: MutationStatus
  activateAccount: (credentials: {
    activationUrl: string
    password: string
    destination?: Destination
  }) => void
  activateAccountMutationErrors: QueryError[]
  activateAccountMutationStatus: MutationStatus
  createAccount: (credentials: {
    email: string
    emailOptIn: boolean
    firstName: string
    lastName: string
    password: string
    destination?: Destination
  }) => void
  createAccountMutationErrors: QueryError[]
  createAccountMutationStatus: MutationStatus
  navigateToLogin: (options: {
    sso?: {
      organizationId: string
      organizationShopifyCustomerTag: string
    }
    redirectUrl?: string
    navigationMethod: 'assign' | 'replace'
  }) => void
}

const useAuthenticationImpl = (): UseAuthenticationOutput => {
  const { personalizedClient } = useGraphqlClients()
  const { pushAccountCreated } = useGoogleTagManager()
  const [cookies, _setCookie, removeCookie] = useCookies([
    cookieKeys.authToken.key,
    cookieKeys.authRefreshToken.key,
  ])

  const authCookie = cookies[cookieKeys.authToken.key]
  const authRefreshCookie = cookies[cookieKeys.authRefreshToken.key]

  const removeAuthCookies = useCallback(() => {
    removeCookie(cookieKeys.authToken.key, {
      path: '/',
    })
    removeCookie(cookieKeys.authRefreshToken.key, {
      path: '/',
    })

    // Temporary code to clean up malformatted cookies
    removeCookie(cookieKeys.authToken.key, {
      path: '/',
      domain: '.www.wearfigs.com',
    })
    removeCookie(cookieKeys.authRefreshToken.key, {
      path: '/',
      domain: '.www.wearfigs.com',
    })
  }, [removeCookie])

  const authenticationStatus = useMemo(() => {
    return convertCookiesToAccountAuthorization({
      authCookie,
      authRefreshCookie,
    })
  }, [authCookie, authRefreshCookie])

  const [logoutMutationStatus, setLogoutMutationStatus] =
    useState<UseAuthenticationOutput['logoutMutationStatus']>('idle')
  const logOut = useCallback<UseAuthenticationOutput['logOut']>(
    options => {
      setLogoutMutationStatus('pending')

      // Clear our auth cookies. This is all we should have to do really.
      removeAuthCookies()

      // It is important to do this hard refresh to make sure all 3rd parties are reset.
      navigateToDestination(options?.destination ?? '/')
    },
    [removeAuthCookies]
  )

  const [activateAccountMutationErrors, setActivateAccountMutationErrors] = useState<
    UseAuthenticationOutput['activateAccountMutationErrors']
  >([])
  const [activateAccountMutationStatus, setActivateAccountMutationStatus] =
    useState<UseAuthenticationOutput['activateAccountMutationStatus']>('idle')
  const [activateAccountMutation] = useMutation(
    gql(`
      mutation ActivateAccount($activationUrl: String!, $password: String!) {
        activateCustomer(activationUrl: $activationUrl, password: $password) {
          refreshToken
          token
        }
      }
  `),
    { client: personalizedClient }
  )

  const activateAccount = useCallback<UseAuthenticationOutput['activateAccount']>(
    ({ activationUrl, password, destination }) => {
      if (activateAccountMutationStatus !== 'pending') {
        setActivateAccountMutationErrors([])
        setActivateAccountMutationStatus('pending')

        activateAccountMutation({
          variables: { activationUrl, password },
        })
          .then(_activateAccountMutationResult => {
            // It is important to do this hard refresh to make sure all 3rd parties are reset.
            navigateToDestination(destination ?? '/myfigs')
          })
          .catch(error => {
            const errors = convertGraphqlErrorToQueryError(error)
            setActivateAccountMutationErrors(errors)
            setActivateAccountMutationStatus('rejected')
          })
      }
    },
    [activateAccountMutation, activateAccountMutationStatus]
  )

  const [loginMutationErrors, setLoginMutationErrors] = useState<
    UseAuthenticationOutput['loginMutationErrors']
  >([])
  const [loginMutationStatus, setLoginMutationStatus] =
    useState<UseAuthenticationOutput['loginMutationStatus']>('idle')
  const [loginMutation] = useMutation(
    gql(`
      mutation LoginUser($email: String!, $password: String!) {
        loginCustomer(email: $email, password: $password) {
          refreshToken
          token
        }
      }
  `),
    { client: personalizedClient }
  )

  const logIn = useCallback<UseAuthenticationOutput['logIn']>(
    ({ email, password, destination }) => {
      if (loginMutationStatus !== 'pending') {
        setLoginMutationErrors([])
        setLoginMutationStatus('pending')
        loginMutation({
          variables: { email, password },
        })
          .then(_loginMutationResult => {
            // It is important to do this hard refresh to make sure all 3rd parties are reset.
            navigateToDestination(destination ?? '/myfigs')
          })
          .catch(error => {
            const errors = convertGraphqlErrorToQueryError(error)
            setLoginMutationErrors(errors)
            setLoginMutationStatus('rejected')
          })
      }
    },
    [loginMutation, loginMutationStatus]
  )

  const [createAccountMutationErrors, setCreateAccountMutationErrors] = useState<
    UseAuthenticationOutput['createAccountMutationErrors']
  >([])
  const [createAccountMutationStatus, setCreateAccountMutationStatus] =
    useState<UseAuthenticationOutput['createAccountMutationStatus']>('idle')
  const [createAccountMutation] = useMutation(
    gql(`
      mutation CreateUser($input: CustomerInput!) {
        createCustomer(input: $input) {
          refreshToken
          token
        }
      }
  `),
    { client: personalizedClient }
  )

  const createAccount = useCallback<UseAuthenticationOutput['createAccount']>(
    ({ email, emailOptIn, firstName, lastName, password, destination }) => {
      if (createAccountMutationStatus !== 'pending') {
        setCreateAccountMutationErrors([])
        setCreateAccountMutationStatus('pending')

        createAccountMutation({
          variables: { input: { email, emailOptIn, firstName, lastName, password } },
        })
          .then(_createAccountMutationResult => {
            pushAccountCreated(email)
            // It is important to do this hard refresh to make sure all 3rd parties are reset.
            navigateToDestination(destination ?? '/account/profile/complete')
          })
          .catch(error => {
            const errors = convertGraphqlErrorToQueryError(error)
            setCreateAccountMutationErrors(errors)
            setCreateAccountMutationStatus('rejected')
          })
      }
    },
    [createAccountMutation, createAccountMutationStatus, pushAccountCreated]
  )

  const navigateToLogin = useCallback<UseAuthenticationOutput['navigateToLogin']>(
    ({ redirectUrl: redirectUrlString, navigationMethod, sso: ssoProp }) => {
      const sso = window.location.search.includes('sso_redirect=true') ? undefined : ssoProp

      const loginSearchParams = new URLSearchParams()
      const loginPath = sso
        ? `/api/account-services/auth/saml/${sso.organizationId}/login`
        : `/account/login`

      const redirectUrl = new URL(
        redirectUrlString ?? `${window.location.protocol}//${window.location.host}/`
      )
      if (sso) {
        redirectUrl.searchParams.set('sso_redirect', 'true')
      }
      loginSearchParams.set('redirectUrl', redirectUrl.toString())

      if (sso?.organizationShopifyCustomerTag) {
        loginSearchParams.set('groupTag', sso.organizationShopifyCustomerTag)
      }

      window.location[navigationMethod](`${loginPath}?${loginSearchParams.toString()}`)
    },
    []
  )

  const refreshAuthTokenPromiseRef = useRef<Promise<void> | null>(null)
  const [refreshAuthTokensStatus, setRefreshAuthTokensStatus] =
    useState<UseAuthenticationOutput['refreshAuthTokensStatus']>('idle')
  const refreshAuthTokens = useCallback(() => {
    if (refreshAuthTokensStatus === 'pending') {
      return refreshAuthTokenPromiseRef.current ?? Promise.resolve()
    } else {
      setRefreshAuthTokensStatus('pending')
    }

    const cookiesThisInstant = new Cookies()
    const isAuthedThisInstant = !!convertCookiesToAccountAuthorization({
      authCookie: cookiesThisInstant.get(cookieKeys.authToken.key) ?? undefined,
      authRefreshCookie: cookiesThisInstant.get(cookieKeys.authRefreshToken.key) ?? undefined,
    })

    refreshAuthTokenPromiseRef.current = new Promise<void>((baseResolve, baseReject) => {
      const reject = () => {
        removeAuthCookies()
        setRefreshAuthTokensStatus('rejected')
        baseReject()
      }
      const resolve = () => {
        setRefreshAuthTokensStatus('resolved')
        baseResolve()
      }
      if (isAuthedThisInstant) {
        personalizedClient
          .mutate({
            mutation: gql(`
              mutation getNewTokens($refreshToken: String!) {
                customerTokenRefresh(refreshToken: $refreshToken) {
                  token
                  refreshToken
                }
              }
            `),
            variables: { refreshToken: authRefreshCookie ?? '' },
          })
          .then(_response => {
            resolve()
          })
          .catch(() => {
            reject()
          })
      } else {
        reject()
      }
    })
    return refreshAuthTokenPromiseRef.current
  }, [authRefreshCookie, personalizedClient, refreshAuthTokensStatus, removeAuthCookies])

  return {
    account: authenticationStatus,
    isLoggedIn: !!authenticationStatus,
    refreshAuthTokens,
    refreshAuthTokensStatus,
    logOut,
    logoutMutationStatus,
    logIn,
    loginMutationErrors,
    loginMutationStatus,
    activateAccount,
    activateAccountMutationErrors,
    activateAccountMutationStatus,
    createAccount,
    createAccountMutationErrors,
    createAccountMutationStatus,
    navigateToLogin,
  }
}

const AuthenticationContainer = createContainer(useAuthenticationImpl)
AuthenticationContainer.Provider.displayName = 'AuthenticationProviderImpl'
export const AuthenticationProvider = AuthenticationContainer.Provider
export const useAuthentication = AuthenticationContainer.useContainer
